import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { catchError, map, Observable, of } from 'rxjs';
import { BaseService } from 'src/app/helpers/base.service';
import { environment } from 'src/environments/environment.development';

export interface iEasyFeeApproval {
  Name: string;
  count: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApprovalNotificationService extends BaseService {

  private readonly _leaveApprovals = `${environment.base_url}/api/count/getpendingleaveapprovalcount`;
  private readonly _jobRequestApprovals = `${environment.recruitmentBaseUrl}/api/JobRequestForm/count`;
  private readonly _jobRequisitionApprovals = `${environment.recruitmentBaseUrl}/api/JobRequisitionForm/count`;
  private readonly _appraisalApprovals = `${environment.appraisal_base_url}/api/FormApproval/GetPendingCount`;
  private readonly _easyFormApprovals = `${environment.easy_form_base_url}/api/FormSubmission/GetPendingApprovalCount`;
  private readonly _assetProApprovals = `${environment.inventory_base_url}/api/Counts/pending-counts`;
  private readonly _easyFeeApprovals = `${environment.easy_fee_base_url}/api/external-osquare/employee`;

  private readonly _options = {
    headers: new HttpHeaders({
      skipStationId: 'true',
      skipCompanyId: 'true'
    })
  };

  constructor(
    private _toastrServ: ToastrService,
  ) {
    super();
  }

  private _lastErrorToastTime = 0;

  private handleApprovalCountError<T>(fallbackValue: T, message: string) {
    return (error: HttpErrorResponse) => {
      // Approval counters are non-critical: do not show noisy toasts for access-restricted users.
      if (error?.status !== 403) {
        const now = Date.now();
        if (now - this._lastErrorToastTime > 10000) {
          this._lastErrorToastTime = now;
          this._toastrServ.error(message);
        }
      }
      return of(fallbackValue);
    };
  }

  GetLeaveApprovals(employeeId: string): Observable<number> {
    return this._http
      .get<number>(`${this._leaveApprovals}?EmployeeId=${employeeId}`, this._options)
      .pipe(
        catchError(this.handleApprovalCountError(0, 'Error fetching leave approval count.'))
      );
  }
  GetAppraisalApprovals(): Observable<number> {
    return this._http
      .get<any>(this._appraisalApprovals, this._options)
      .pipe(
        map((response) => response?.result?.count ?? 0),
        catchError(this.handleApprovalCountError(0, 'Error fetching appraisal approval count.'))
      );
  }
  GetEasyFormApprovals(): Observable<number> {
    return this._http
      .get<any>(this._easyFormApprovals, this._options)
      .pipe(
        map((response) => response?.result ?? 0),
        catchError(this.handleApprovalCountError(0, 'Error fetching easy form approval count.'))
      );
  }
  GetJobRequestApprovals(): Observable<number> {
    return this._http
      .get<any>(this._jobRequestApprovals, this._options)
      .pipe(
        map((response) => response?.total ?? 0),
        catchError(this.handleApprovalCountError(0, 'Error fetching job request approval count.'))
      );
  }
  GetJobRequisitionApprovals(): Observable<number> {
    return this._http
      .get<any>(this._jobRequisitionApprovals, this._options)
      .pipe(
        map((response) => response?.total ?? 0),
        catchError(this.handleApprovalCountError(0, 'Error fetching job requisition approval count.'))
      );
  }
  GetAssetProApprovals(userId: string): Observable<any> {
    return this._http
      .get<any>(`${this._assetProApprovals}?userId=${userId}`, this._options)
      .pipe(
        map((res) => ({
          prCount: res?.prCount ?? 0,
          procurementCount: res?.procurementCount ?? 0,
          poCount: res?.poCount ?? 0,
        })),
        catchError(this.handleApprovalCountError({
            prCount: 0,
            procurementCount: 0,
            poCount: 0,
          }, 'Error fetching asset/pro approval counts.'))
      );
  }
  GetEasyFeeApprovals(employeeId: string): Observable<iEasyFeeApproval[]> {
    return this._http
      .get<iEasyFeeApproval[]>(
        `${this._easyFeeApprovals}?employee_id=${employeeId}`,
        this._options
      )
      .pipe(
        catchError(this.handleApprovalCountError([], 'Error fetching easy fee approval count.'))
      );
  }
}
